/**
 * UserGuiding injects a floating #ug-banner-frame over the page. We do not
 * shift app layout for it. This module only:
 * - insets the frame to the right of the sidebar (--ug-banner-left)
 * - publishes --ug-banner-height for optional overlap offsets
 * - hides UG's spacer so a "push" banner cannot steal vertical space
 * - strips transform/filter/perspective on html/body/app-root so position:fixed
 *   stays viewport-relative (those properties create a containing block)
 *
 * styles.scss then pins #ug-banner-frame with left/width/z-index.
 */

const BANNER_FRAME_ID = 'ug-banner-frame'
const BANNER_SPACER_ID = 'userguiding-banner-spacer'

function isBannerVisible(frame: HTMLElement | null): frame is HTMLElement {
  if (!frame) {
    return false
  }
  return getComputedStyle(frame).display !== 'none'
}

/** Height actually on screen (UG eases in from top: negative). */
function visibleBannerHeight(frame: HTMLElement): number {
  const rect = frame.getBoundingClientRect()
  return Math.max(0, Math.round(Math.min(rect.height, rect.bottom)))
}

function isBannerAnimating(frame: HTMLElement): boolean {
  const cs = getComputedStyle(frame)
  const transform = cs.transform
  const identity =
    transform === 'none' || transform === 'matrix(1, 0, 0, 1, 0, 0)'
  const opacity = Number(cs.opacity)
  const target = parseFloat(frame.style.height)
  const rect = frame.getBoundingClientRect()
  const heightSettled =
    Number.isNaN(target) || Math.abs(rect.height - target) < 0.5
  const topSettled = Math.abs(rect.top) < 0.5
  const opacitySettled = opacity < 0.01 || opacity > 0.99
  return !identity || !heightSettled || !topSettled || !opacitySettled
}

function sidebarEl(doc: Document): HTMLElement | null {
  return (
    doc.querySelector<HTMLElement>('.ht-nav') ??
    doc.querySelector<HTMLElement>('nav')
  )
}

function clearBannerContainingBlock(el: HTMLElement | null, active: boolean) {
  if (!el) {
    return
  }
  if (active) {
    if (el.style.getPropertyValue('transform') !== 'none') {
      el.style.setProperty('transform', 'none', 'important')
    }
    if (el.style.getPropertyValue('filter') !== 'none') {
      el.style.setProperty('filter', 'none', 'important')
    }
    if (el.style.getPropertyValue('perspective') !== 'none') {
      el.style.setProperty('perspective', 'none', 'important')
    }
    return
  }
  if (el.style.getPropertyValue('transform')) {
    el.style.removeProperty('transform')
  }
  if (el.style.getPropertyValue('filter')) {
    el.style.removeProperty('filter')
  }
  if (el.style.getPropertyValue('perspective')) {
    el.style.removeProperty('perspective')
  }
}

export function syncUserGuidingBannerLayout(doc: Document = document): void {
  const root = doc.documentElement
  const frame = doc.getElementById(BANNER_FRAME_ID)
  const spacer = doc.getElementById(BANNER_SPACER_ID)
  const nav = sidebarEl(doc)
  const visible = isBannerVisible(frame)
  const height = visible ? visibleBannerHeight(frame) : 0
  const left =
    visible && nav ? Math.round(nav.getBoundingClientRect().width) : 0

  root.style.setProperty('--ug-banner-height', `${height}px`)
  root.style.setProperty('--ug-banner-left', `${left}px`)
  root.classList.toggle('ug-banner-visible', visible)

  if (spacer) {
    spacer.style.setProperty('display', 'none', 'important')
    spacer.style.setProperty('height', '0', 'important')
    spacer.style.setProperty('min-height', '0', 'important')
    spacer.style.setProperty('max-height', '0', 'important')
  }

  clearBannerContainingBlock(root, visible)
  clearBannerContainingBlock(doc.body, visible)
  clearBannerContainingBlock(doc.querySelector('app-root'), visible)
}

let boundDoc: Document | null = null

/** Watch banner/sidebar inject, resize, and UG style mutations. */
export function bindUserGuidingBannerLayout(
  doc: Document = document
): () => void {
  if (boundDoc === doc) {
    return () => undefined
  }
  boundDoc = doc

  let rafId = 0
  let applyingLayout = false
  const observed = new Set<Element>()
  const resizeObserver = new ResizeObserver(() => {
    syncAndTick()
  })

  const observe = (el: Element | null) => {
    if (!el || observed.has(el)) {
      return
    }
    observed.add(el)
    resizeObserver.observe(el)
  }

  const tick = () => {
    syncUserGuidingBannerLayout(doc)
    const frame = doc.getElementById(BANNER_FRAME_ID)
    if (frame && isBannerVisible(frame) && isBannerAnimating(frame)) {
      rafId = requestAnimationFrame(tick)
    }
  }

  const syncAndTick = () => {
    // Writing spacer/containing-block styles retriggers the MutationObserver.
    if (applyingLayout) {
      return
    }
    applyingLayout = true
    cancelAnimationFrame(rafId)
    syncUserGuidingBannerLayout(doc)
    observe(doc.getElementById(BANNER_FRAME_ID))
    observe(sidebarEl(doc))
    const frame = doc.getElementById(BANNER_FRAME_ID)
    // Keep --ug-banner-height in sync while UG eases the iframe in.
    if (frame && isBannerVisible(frame) && isBannerAnimating(frame)) {
      rafId = requestAnimationFrame(tick)
    }
    applyingLayout = false
  }

  const treeObserver = new MutationObserver(syncAndTick)
  treeObserver.observe(doc.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
  })
  syncAndTick()

  return () => {
    cancelAnimationFrame(rafId)
    treeObserver.disconnect()
    resizeObserver.disconnect()
    if (boundDoc === doc) {
      boundDoc = null
    }
  }
}
